/**
 * RBAC-4.2 — copy a colleague's access onto a person.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the rows mean is `lib/personAccessCopy.ts`; who may copy what
 * is the database's (`20261102a1`: roles.administer, both people homed in the caller's current
 * account, and assign_person_role's triggers for every grant refusal). Nothing here passes a tenant.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  COPY_PERSON_ACCESS_RPC,
  PERSON_ACCESS_COPY_PREVIEW_RPC,
  PERSON_ACCESS_COPY_SOURCES_RPC,
  copyPersonAccessArgs,
  parseCopyPreview,
  parseCopySources,
  type CopyPreview,
  type CopySource,
} from '@/lib/personAccessCopy';
import type { PersonRoleAssignment } from '@/lib/personRole';
import { PersonRoleError } from './personRoleService';

const toError = (e: { message?: string; code?: string; hint?: string }) =>
  new PersonRoleError({ message: e.message, code: e.code, hint: e.hint });

export async function fetchCopySources(targetUserId: string): Promise<CopySource[]> {
  const { data, error } = await supabase.rpc(PERSON_ACCESS_COPY_SOURCES_RPC, { p_target_user_id: targetUserId });
  if (error) throw toError(error);
  return parseCopySources(data);
}

export async function fetchCopyPreview(targetUserId: string, sourceUserId: string): Promise<CopyPreview> {
  const { data, error } = await supabase.rpc(PERSON_ACCESS_COPY_PREVIEW_RPC, {
    p_target_user_id: targetUserId,
    p_source_user_id: sourceUserId,
  });
  if (error) throw toError(error);
  const parsed = parseCopyPreview(data);
  if (!parsed) throw new PersonRoleError({ message: 'The difference came back in a shape this screen does not read.' });
  return parsed;
}

export async function copyPersonAccess(target: PersonRoleAssignment, preview: CopyPreview): Promise<number | null> {
  const { data, error } = await supabase.rpc(COPY_PERSON_ACCESS_RPC, copyPersonAccessArgs(target, preview));
  if (error) throw toError(error);
  const roleId = (data as { role_id?: unknown } | null)?.role_id;
  return typeof roleId === 'number' ? roleId : null;
}
